import { inr, dmy } from "./format";
import { API_BASE } from "./api";

export const qrUrl = (value, size = 10) => `${API_BASE}/qr.png?value=${encodeURIComponent(value)}&size=${size}`;

const openDoc = (html, w = 900, h = 800) => {
  const win = window.open("", "_blank", `width=${w},height=${h}`);
  if (!win) return false;
  win.document.write(html);
  win.document.close();
  return true;
};

const logoTag = (shop, height = 46, thermal = false) => {
  const src = thermal ? shop?.logo_thermal || shop?.logo : shop?.logo;
  if (!src) return `<div class="brandtext">BDS</div>`;
  return `<img class="logo" src="${src}" style="height:${height}px;width:auto;object-fit:contain" alt="BDS" />`;
};

const shopLines = (shop) =>
  [
    shop?.address,
    shop?.phone ? `Mob: ${shop.phone}${shop?.alt_phone ? " / " + shop.alt_phone : ""}` : "",
    shop?.email,
    shop?.website,
    shop?.gstin ? `GSTIN: ${shop.gstin}` : "",
  ]
    .filter(Boolean)
    .map((l) => `<div class="muted">${l}</div>`)
    .join("");

const baseCss = `
  body{font-family:'Helvetica Neue',Arial,sans-serif;color:#0F172A;margin:0}
  .muted{color:#64748B;font-size:11px}
  .brandtext{font-weight:800;font-size:22px;color:#4338CA;letter-spacing:-0.5px}
  .r{text-align:right}.c{text-align:center}
  table{width:100%;border-collapse:collapse}
  th,td{border-bottom:1px solid #E2E8F0;padding:6px 8px;text-align:left;font-size:11px}
  th{background:#F1F5F9;text-transform:uppercase;font-size:9px;letter-spacing:0.5px}
  .qrcell img{display:block}
  @media print{ .noprint{display:none} }
`;

const autoPrint = (auto = true) =>
  auto ? `<script>window.onload=function(){window.focus();window.print();}<\/script>` : "";

/* ------------------------------------------------------------------ *
 * 58mm / 80mm thermal receipt — purpose-built layout, never scaled A4
 * ------------------------------------------------------------------ */
export function receiptHtml(inv, shop, printer = {}, size = "58mm", isAdmin = false) {
  const width = size === "80mm" ? 76 : 54; // printable mm
  const m = {
    t: printer.margin_top ?? 2,
    b: printer.margin_bottom ?? 4,
    l: printer.margin_left ?? 2,
    r: printer.margin_right ?? 2,
  };
  const showLogo = printer.print_logo !== false;
  const showHeader = printer.print_header !== false;
  const showFooter = printer.print_footer !== false;
  const pay = inv.receipt_payment_config || {};
  const showFee = pay.show_fee_on_receipt !== false;
  const maskRef = pay.mask_reference_on_receipt !== false;

  const items = inv.items
    .map(
      (it) => `<div class="item">
        <div class="pname">${it.product_name}</div>
        <div class="prow"><span>${it.stk_code || it.sku || ""} ${it.qty} × ${inr(it.rate)}</span><span>${inr(it.line_total)}</span></div>
        ${Number(it.discount) ? `<div class="prow small"><span>disc</span><span>- ${inr(it.discount)}</span></div>` : ""}
      </div>`
    )
    .join("");

  const line = (label, value, cls = "") =>
    `<div class="trow ${cls}"><span>${label}</span><span>${value}</span></div>`;

  return `<!doctype html><html><head><meta charset="utf-8"/><title>${inv.invoice_no}</title>
  <style>
    @page{size:${size} auto;margin:${m.t}mm ${m.r}mm ${m.b}mm ${m.l}mm}
    *{box-sizing:border-box}
    body{width:${width}mm;margin:0;font-family:'Courier New',monospace;color:#000;font-size:11px;line-height:1.35;-webkit-print-color-adjust:exact}
    .c{text-align:center}.r{text-align:right}
    .shop{font-size:14px;font-weight:700;letter-spacing:0.4px}
    .muted{font-size:9px}
    .logo{max-width:${width - 8}mm;height:auto;margin:0 auto 2px;display:block;filter:grayscale(1) contrast(1.4)}
    .brandtext{font-weight:800;font-size:18px;text-align:center}
    hr{border:none;border-top:1px dashed #000;margin:4px 0}
    .kv{display:flex;justify-content:space-between;gap:4px;font-size:10px}
    .item{margin-bottom:3px}
    .pname{font-weight:700;word-wrap:break-word;overflow-wrap:anywhere}
    .prow{display:flex;justify-content:space-between;gap:6px;font-size:10px}
    .small{font-size:9px}
    .trow{display:flex;justify-content:space-between;gap:6px;font-size:10px}
    .grand{font-size:14px;font-weight:800;border-top:1px solid #000;border-bottom:1px solid #000;padding:3px 0;margin:3px 0}
    .foot{text-align:center;font-size:9px;margin-top:5px}
    .qr{display:block;margin:4px auto 0}
  </style></head><body>
  ${showLogo ? `<div class="c">${logoTag(shop, 48, true)}</div>` : ""}
  ${showHeader ? `<div class="c shop">${(shop?.name || "BHARAT DIGITAL SEVA").toUpperCase()}</div>
    <div class="c muted">${shop?.address || ""}</div>
    <div class="c muted">${shop?.phone ? "Mob: " + shop.phone : ""}${shop?.alt_phone ? " / " + shop.alt_phone : ""}</div>
    <div class="c muted">${shop?.email || ""}</div>
    <div class="c muted">${shop?.gstin ? "GSTIN: " + shop.gstin : ""}</div>` : ""}
  <hr/>
  <div class="kv"><span>Invoice</span><span><b>${inv.invoice_no}</b></span></div>
  <div class="kv"><span>Date</span><span>${dmy(inv.date)} ${inv.time}</span></div>
  ${inv.customer_name ? `<div class="kv"><span>Customer</span><span>${inv.customer_name}</span></div>` : ""}
  ${inv.customer_mobile ? `<div class="kv"><span>Mobile</span><span>${inv.customer_mobile}</span></div>` : ""}
  <div class="kv"><span>Cashier</span><span>${inv.cashier || "-"}</span></div>
  <div class="kv"><span>Billing Agent</span><span>${inv.billing_agent_name_snapshot || inv.cashier || "-"}</span></div>
  ${(inv.payments || []).map((p) => {
    const d = p.details || {};
    const bits = [];
    if (d.provider) bits.push(d.provider);
    if (d.card_type) bits.push(d.card_type);
    if (d.card_batch_number) bits.push(`Batch ${d.card_batch_number}`);
    if (d.card_last4) bits.push(`****${d.card_last4}`);
    if (d.bank_transfer_mode) bits.push(d.bank_transfer_mode);
    const ref = d.utr_reference || d.transaction_reference || d.cheque_number || "";
    if (ref) bits.push(`Ref ${maskRef ? "****" + String(ref).slice(-4) : ref}`);
    return bits.length
      ? `<div class="kv"><span>${p.method}</span><span>${bits.join(" · ")}</span></div>`
      : "";
  }).join("")}
  ${inv.card_fee_amount && showFee ? `<div class="kv"><span>${inv.card_fee_card_type || "Card"} Fee (${inv.card_fee_rate}%)</span><span>${inr(inv.card_fee_amount)}</span></div>` : ""}
  <div class="kv"><span>Payment</span><span>${inv.payment_method} (${inv.payment_status})</span></div>
  ${inv.status === "VOID" ? `<div class="c"><b>*** VOID ***</b></div>` : ""}
  <hr/>
  ${items}
  <hr/>
  ${line("Subtotal", inr(inv.subtotal))}
  ${Number(inv.auto_discount) ? line(`Auto Discount${inv.discount_percent ? ` (${inv.discount_percent}%)` : ""}`, "- " + inr(inv.auto_discount)) : ""}
  ${Number(inv.auto_discount) && (inv.auto_discount_rules || []).length ? line("  " + inv.auto_discount_rules.join(", "), "", "small") : ""}
  ${Number(inv.auto_discount) ? line("Product Net", inr(inv.product_net ?? (Number(inv.subtotal) - Number(inv.auto_discount)))) : ""}
  ${Number(inv.manual_discount) ? line(`Manual Discount${inv.manual_discount_percent ? ` (${inv.manual_discount_percent}%)` : ""}`, "- " + inr(inv.manual_discount)) : ""}
  ${inv.manual_discount_reason ? line("  Reason: " + inv.manual_discount_reason, "", "small") : ""}
  ${Number(inv.discount_amount) ? line("Discounted Amount", inr(inv.amount_after_discount)) : ""}
  ${Number(inv.tax_amount) ? line("Tax", inr(inv.tax_amount)) : ""}
  ${inv.order_type === "DELIVERY" ? line("Order Type", "Delivery") : ""}
  ${Number(inv.delivery_charge) ? line("Delivery Charge", inr(inv.delivery_charge)) : ""}
  ${Number(inv.round_off) ? line("Round Off", inr(inv.round_off)) : ""}
  <div class="trow grand"><span>GRAND TOTAL</span><span>${inr(inv.grand_total)}</span></div>
  ${line("Amount Paid", inr(inv.amount_paid))}
  ${(inv.payments || []).map((p) => line(`  ${p.method}`, inr(p.amount), "small")).join("")}
  ${Number(inv.change_due) ? line("Change", inr(inv.change_due)) : ""}
  ${Number(inv.due_amount) ? line("Balance Due", inr(inv.due_amount)) : ""}
  ${isAdmin && inv.actual_profit != null ? line("Profit (admin)", inr(inv.actual_profit)) : ""}
  ${showFooter ? `<div class="foot"><b>${shop?.invoice_footer || "Thank you for shopping with Bharat Digital Seva."}</b>
    <div>Please visit again.</div>
    <div class="muted">${shop?.terms || ""}</div></div>` : ""}
  <img class="qr" src="${qrUrl("BDS:INVOICE:" + inv.invoice_no, 4)}" width="90" height="90" alt="QR"/>
  ${autoPrint()}
  </body></html>`;
}

export function printReceipt(inv, shop, printer = {}, size, isAdmin = false) {
  const paper = size || printer?.printer_type || "58mm";
  if (paper === "A4") return printInvoice(inv, shop, "A4", isAdmin);
  return openDoc(receiptHtml(inv, shop, printer, paper, isAdmin), 420, 760);
}

/* ------------------------------------------------------------------ *
 * A4 tax invoice
 * ------------------------------------------------------------------ */
export function invoiceHtml(inv, shop, size = "A4", isAdmin = false) {
  const rows = inv.items
    .map(
      (it, i) => `<tr>
      <td>${i + 1}</td><td>${it.product_name}</td><td>${it.stk_code || it.sku || "-"}</td>
      <td class="r">${it.qty}</td><td>${it.unit || "PCS"}</td><td class="r">${inr(it.rate)}</td>
      <td class="r">${inr(it.discount)}</td><td class="r">${inr(it.tax)}</td><td class="r">${inr(it.line_total)}</td></tr>`
    )
    .join("");
  const totalRow = (label, value, bold) =>
    `<tr class="${bold ? "bold" : ""}"><td colspan="8" class="r">${label}</td><td class="r">${value}</td></tr>`;

  return `<!doctype html><html><head><meta charset="utf-8"/><title>${inv.invoice_no}</title>
  <style>@page{size:A4;margin:12mm}${baseCss}
    .head{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #4338CA;padding-bottom:8px;margin-bottom:10px}
    .badge{display:inline-block;border:1px solid #4338CA;color:#4338CA;padding:2px 8px;border-radius:20px;font-size:10px}
    .bold td{font-weight:700;font-size:13px}
    .meta{display:flex;gap:32px;margin:10px 0}
    .footer{margin-top:16px;text-align:center;border-top:1px dashed #94A3B8;padding-top:8px}
    h1{font-size:20px;margin:0 0 2px;color:#3730A3}
  </style></head><body>
  <div class="head">
    <div>${logoTag(shop, 54)}<h1>${shop?.name || "Bharat Digital Seva"}</h1>${shopLines(shop)}</div>
    <div class="r">
      <div class="badge">${inv.status === "VOID" ? "VOID" : "TAX INVOICE"}</div>
      <div><b>${inv.invoice_no}</b></div><div class="muted">${dmy(inv.date)} ${inv.time}</div>
      <img src="${qrUrl("BDS:INVOICE:" + inv.invoice_no, 5)}" width="80" height="80" alt="QR"/>
    </div>
  </div>
  <div class="meta">
    <div><b>Customer:</b> ${inv.customer_name || "Walk-in Customer"}<br/>
      ${inv.customer_mobile ? "Mobile: " + inv.customer_mobile + "<br/>" : ""}
      ${inv.customer_whatsapp ? "WhatsApp: " + inv.customer_whatsapp + "<br/>" : ""}
      ${inv.customer_address ? inv.customer_address + "<br/>" : ""}
      ${inv.customer_id ? "Customer ID: " + inv.customer_id : ""}</div>
    <div><b>Payment:</b> ${inv.payment_method} (${inv.payment_status})<br/>
      <b>Cashier:</b> ${inv.cashier || "-"}<br/><b>Billing Agent:</b> ${inv.billing_agent_name_snapshot || inv.cashier || "-"}<br/>${inv.due_date ? "<b>Due Date:</b> " + dmy(inv.due_date) : ""}</div>
  </div>
  <table><thead><tr><th>SL</th><th>Product</th><th>STK/SKU</th><th class="r">Qty</th><th>Unit</th>
    <th class="r">Rate</th><th class="r">Discount</th><th class="r">Tax</th><th class="r">Amount</th></tr></thead>
  <tbody>${rows}
    ${totalRow("Subtotal", inr(inv.subtotal))}
    ${Number(inv.auto_discount) ? totalRow(`Automatic Discount${inv.discount_percent ? ` (${inv.discount_percent}%)` : ""}${(inv.auto_discount_rules || []).length ? " — " + inv.auto_discount_rules.join(", ") : ""}`, "- " + inr(inv.auto_discount)) : ""}
    ${Number(inv.auto_discount) ? totalRow("Product Net", inr(inv.product_net ?? (Number(inv.subtotal) - Number(inv.auto_discount)))) : ""}
    ${Number(inv.manual_discount) ? totalRow(`Manual Discount${inv.manual_discount_percent ? ` (${inv.manual_discount_percent}%)` : ""}${inv.manual_discount_reason ? " — " + inv.manual_discount_reason : ""}`, "- " + inr(inv.manual_discount)) : ""}
    ${Number(inv.discount_amount) ? totalRow("Discounted Amount", inr(inv.amount_after_discount)) : ""}
    ${Number(inv.tax_amount) ? totalRow("Tax", inr(inv.tax_amount)) : ""}
    ${Number(inv.delivery_charge) ? totalRow("Delivery Charge", inr(inv.delivery_charge)) : ""}
    ${Number(inv.round_off) ? totalRow("Round Off", inr(inv.round_off)) : ""}
    ${totalRow("Grand Total", inr(inv.grand_total), true)}
    ${totalRow("Amount Paid", inr(inv.amount_paid))}
    ${Number(inv.change_due) ? totalRow("Change Returned", inr(inv.change_due)) : ""}
    ${Number(inv.due_amount) ? totalRow("Balance Due", inr(inv.due_amount)) : ""}
    ${isAdmin && inv.actual_profit != null ? totalRow("Profit (admin only)", inr(inv.actual_profit)) : ""}
  </tbody></table>
  <div class="footer"><b>${shop?.invoice_footer || "Thank you for shopping with Bharat Digital Seva."}</b>
    <div class="muted">${shop?.terms || ""}</div></div>
  ${autoPrint()}</body></html>`;
}

export function printInvoice(inv, shop, size = "A4", isAdmin = false, printer = {}) {
  if (size !== "A4") return printReceipt(inv, shop, printer, size, isAdmin);
  return openDoc(invoiceHtml(inv, shop, size, isAdmin));
}

/* ------------------------------------------------------------------ *
 * Credit note
 * ------------------------------------------------------------------ */
export function printCreditNote(ret, shop) {
  const rows = ret.items
    .map(
      (it, i) =>
        `<tr><td>${i + 1}</td><td>${it.product_name}</td><td class="r">${it.qty}</td><td class="r">${inr(it.unit_effective)}</td><td class="r">${inr(it.refund_amount)}</td></tr>`
    )
    .join("");
  openDoc(`<!doctype html><html><head><meta charset="utf-8"/><title>${ret.credit_note_no}</title>
  <style>@page{size:A4;margin:12mm}${baseCss} h1{color:#3730A3;margin:0}</style></head><body>
  ${logoTag(shop, 54)}<h1>${shop?.name || "Bharat Digital Seva"}</h1>${shopLines(shop)}
  <h2>Credit Note ${ret.credit_note_no}</h2>
  <div class="muted">Return ID: ${ret.return_id} | Invoice: ${ret.invoice_no} | Date: ${dmy(ret.date)} ${ret.time || ""}</div>
  <div class="muted">Customer: ${ret.customer_name || "Walk-in"} ${ret.customer_mobile || ""}</div>
  <table><thead><tr><th>SL</th><th>Product</th><th class="r">Qty</th><th class="r">Effective Rate</th><th class="r">Refund</th></tr></thead>
  <tbody>${rows}<tr><td colspan="4" class="r"><b>Total Refund</b></td><td class="r"><b>${inr(ret.refund_amount)}</b></td></tr></tbody></table>
  <p class="muted">Refund Method: ${ret.refund_method} | Reference: ${ret.refund_reference} | Authorized By: ${ret.authorized_by}</p>
  <p class="muted">${shop?.invoice_footer || "Thank you for shopping with Bharat Digital Seva."}</p>
  ${autoPrint()}</body></html>`);
}

/* ------------------------------------------------------------------ *
 * Inventory / MRP A4 report with product QR codes
 * ------------------------------------------------------------------ */
const COLUMN_DEFS = {
  name: { label: "Item Name", value: (r) => r.name },
  product_id: { label: "Product ID", value: (r) => r.product_id },
  sku: { label: "SKU", value: (r) => r.sku || "-" },
  stk_code: { label: "STK Code", value: (r) => r.stk_code || "-" },
  category: { label: "Category", value: (r) => r.category || "-" },
  brand: { label: "Brand", value: (r) => r.brand || "-" },
  stock: { label: "Available Stock", value: (r) => `${r.stock} ${r.unit || ""}`, cls: "r" },
  mrp: { label: "MRP", value: (r) => inr(r.mrp), cls: "r" },
  selling_price: { label: "Selling Price", value: (r) => inr(r.selling_price), cls: "r" },
  status: { label: "Status", value: (r) => r.status },
  barcode: { label: "Barcode", value: (r) => r.barcode || "-" },
};

export function inventoryReportHtml(report, shop, options = {}) {
  const {
    title = "AVAILABLE STOCK INVENTORY REPORT",
    columns = ["name", "product_id", "stk_code", "stock", "mrp", "qr"],
    qrSizeMm = 20,
    showLogo = true,
    showFooter = true,
  } = options;
  const cols = columns.filter((c) => c === "qr" || COLUMN_DEFS[c]);
  const rowsPerPage = 14;
  const pages = [];
  for (let i = 0; i < report.rows.length; i += rowsPerPage) pages.push(report.rows.slice(i, i + rowsPerPage));
  if (!pages.length) pages.push([]);
  const now = new Date();

  const head = `<tr><th>SL</th>${cols
    .map((c) => (c === "qr" ? `<th class="c">QR Code</th>` : `<th class="${COLUMN_DEFS[c].cls || ""}">${COLUMN_DEFS[c].label}</th>`))
    .join("")}</tr>`;

  const pageHtml = (rows, idx) => `
    <section class="page">
      <div class="rhead">
        ${showLogo ? `<div>${logoTag(shop, 58)}</div>` : ""}
        <div class="rtitle">
          <div class="bname">${(shop?.name || "BHARAT DIGITAL SEVA").toUpperCase()}</div>
          <div class="rt">${title}</div>
          ${shopLines(shop)}
        </div>
        <div class="rmeta muted">
          <div>Generated: ${now.toLocaleDateString("en-IN")}</div>
          <div>Time: ${now.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true })}</div>
          <div>Total Products: <b>${report.total_products}</b></div>
          <div>Total Units: <b>${report.total_units}</b></div>
          <div>Page ${idx + 1} of ${pages.length}</div>
        </div>
      </div>
      <table><thead>${head}</thead><tbody>
        ${rows
          .map(
            (r, i) => `<tr>
            <td>${idx * rowsPerPage + i + 1}</td>
            ${cols
              .map((c) =>
                c === "qr"
                  ? `<td class="c qrcell"><img src="${qrUrl(r.qr_value, 6)}" style="width:${qrSizeMm}mm;height:${qrSizeMm}mm" alt="${r.product_id}"/><div class="qrlabel">${r.stk_code || r.product_id}</div></td>`
                  : `<td class="${COLUMN_DEFS[c].cls || ""}">${COLUMN_DEFS[c].value(r)}</td>`
              )
              .join("")}
          </tr>`
          )
          .join("")}
        ${rows.length === 0 ? `<tr><td colspan="${cols.length + 1}" class="c">No products for the selected filter.</td></tr>` : ""}
      </tbody></table>
      ${showFooter ? `<div class="rfoot muted">Generated by Bharat Digital Seva Stock Management System · ${now.toLocaleString("en-IN")} · Page ${idx + 1} of ${pages.length}</div>` : ""}
    </section>`;

  return `<!doctype html><html><head><meta charset="utf-8"/><title>${title}</title>
  <style>@page{size:A4;margin:10mm}${baseCss}
    .page{page-break-after:always;padding-bottom:6mm}
    .page:last-child{page-break-after:auto}
    .rhead{display:flex;gap:14px;align-items:flex-start;border-bottom:2px solid #4338CA;padding-bottom:8px;margin-bottom:8px}
    .rtitle{flex:1}
    .bname{font-size:17px;font-weight:800;color:#3730A3;letter-spacing:0.4px}
    .rt{font-size:12px;font-weight:700;letter-spacing:1px;margin:2px 0 4px}
    .rmeta{text-align:right;min-width:130px;line-height:1.6}
    td{vertical-align:middle}
    .qrcell{width:${qrSizeMm + 6}mm}
    .qrlabel{font-size:7px;color:#475569;text-align:center;margin-top:1px}
    .rfoot{border-top:1px dashed #94A3B8;margin-top:6px;padding-top:4px;text-align:center}
  </style></head><body>
  ${pages.map(pageHtml).join("")}
  ${autoPrint()}</body></html>`;
}

export function printInventoryReport(report, shop, options) {
  return openDoc(inventoryReportHtml(report, shop, options), 1000, 800);
}

/* ------------------------------------------------------------------ *
 * Single product QR document
 * ------------------------------------------------------------------ */
export function printProductQr(p, shop, includeBarcode = false) {
  openDoc(`<!doctype html><html><head><meta charset="utf-8"/><title>${p.name} QR</title>
  <style>@page{size:A4;margin:16mm}${baseCss}
    .wrap{text-align:center;border:1px solid #E2E8F0;border-radius:12px;padding:24px;max-width:340px;margin:0 auto}
    .pn{font-size:18px;font-weight:800;margin:10px 0 2px}
    .kv{font-size:12px;color:#475569}
    .mrp{font-size:22px;font-weight:800;margin-top:8px}
  </style></head><body>
  <div class="wrap">
    ${logoTag(shop, 60)}
    <div class="kv"><b>${shop?.name || "Bharat Digital Seva"}</b></div>
    <div class="pn">${p.name}</div>
    <div class="kv">Product ID: ${p.product_id}</div>
    <div class="kv">STK: ${p.stk_code || "-"} ${p.sku ? " · SKU: " + p.sku : ""}</div>
    <img src="${qrUrl(p.qr_value || "BDS:PRODUCT:" + p.product_id, 8)}" style="width:45mm;height:45mm;margin-top:12px" alt="QR"/>
    <div class="kv">${p.qr_value || "BDS:PRODUCT:" + p.product_id}</div>
    ${includeBarcode && p.barcode ? `<div class="kv" style="font-family:monospace;font-size:16px;letter-spacing:-1px">${"|".repeat(28)}<br/>${p.barcode}</div>` : ""}
    <div class="mrp">MRP: ${inr(p.mrp)}</div>
  </div>
  ${autoPrint()}</body></html>`);
}

/* ------------------------------------------------------------------ *
 * Product / QR labels (A4 label sheet)
 * ------------------------------------------------------------------ */
export function printLabels(items, shop, opts = {}) {
  const {
    labelSize = "50x30",
    showName = true,
    showMrp = true,
    showStk = true,
    showBarcode = false,
    showLogo = true,
    qrOnly = false,
    columns = 4,
  } = opts;
  const [w, h] = labelSize.split("x").map(Number);
  const cells = items
    .flatMap((it) => Array.from({ length: it.copies || 1 }, () => it))
    .map(
      (p) => `<div class="label">
      ${qrOnly ? "" : showLogo ? `<div class="lg">${logoTag(shop, 12, true)}</div>` : `<div class="bn">${shop?.name || "Bharat Digital Seva"}</div>`}
      ${!qrOnly && showName ? `<div class="pn">${p.name}</div>` : ""}
      <img class="qr" src="${qrUrl(p.qr_value || "BDS:PRODUCT:" + p.product_id, 6)}" alt="qr"/>
      ${!qrOnly && showStk ? `<div class="sk">STK: ${p.stk_code || p.product_id}</div>` : ""}
      ${!qrOnly && showBarcode && p.barcode ? `<div class="sk">${p.barcode}</div>` : ""}
      ${!qrOnly && showMrp ? `<div class="mrp">MRP ${inr(p.mrp)}</div>` : ""}
    </div>`
    )
    .join("");

  openDoc(`<!doctype html><html><head><meta charset="utf-8"/><title>Product QR Labels</title>
  <style>@page{size:A4;margin:8mm}
    body{font-family:Arial,sans-serif;margin:0;display:grid;grid-template-columns:repeat(${columns},1fr);gap:3mm}
    .label{width:${w}mm;height:${h}mm;border:1px dashed #94A3B8;padding:1.5mm;box-sizing:border-box;text-align:center;
      display:flex;flex-direction:column;align-items:center;justify-content:center;overflow:hidden;page-break-inside:avoid}
    .bn{font-size:6px;font-weight:700;color:#000}
    .lg img{max-height:5mm;width:auto}
    .pn{font-size:7.5px;font-weight:700;line-height:1.1;max-height:6mm;overflow:hidden}
    .qr{width:${Math.min(w, h) - 12}mm;height:${Math.min(w, h) - 12}mm;margin:0.5mm 0}
    .sk{font-size:6px;color:#111}
    .mrp{font-size:8.5px;font-weight:800}
  </style></head><body>${cells}${autoPrint()}</body></html>`, 1000, 800);
}

/* ------------------------------------------------------------------ *
 * Generic report table (with BDS branding)
 * ------------------------------------------------------------------ */
export function printTable(title, columns, rows, shop) {
  const head = columns.map((c) => `<th>${c.label}</th>`).join("");
  const body = rows
    .map((r) => `<tr>${columns.map((c) => `<td>${c.format ? c.format(r[c.key], r) : r[c.key] ?? ""}</td>`).join("")}</tr>`)
    .join("");
  const now = new Date();
  openDoc(`<!doctype html><html><head><meta charset="utf-8"/><title>${title}</title>
  <style>@page{size:A4 landscape;margin:10mm}${baseCss}
    .rhead{display:flex;gap:14px;align-items:center;border-bottom:2px solid #4338CA;padding-bottom:8px;margin-bottom:8px}
    .bname{font-size:16px;font-weight:800;color:#3730A3}
    td,th{border:1px solid #E2E8F0}
  </style></head><body>
  <div class="rhead">${logoTag(shop, 46)}
    <div><div class="bname">${shop?.name || "Bharat Digital Seva"}</div>
    <div class="muted">${title} · generated ${now.toLocaleString("en-IN")}</div></div></div>
  <table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>
  <div class="muted" style="text-align:center;margin-top:8px">Generated by Bharat Digital Seva Stock Management System</div>
  ${autoPrint()}</body></html>`, 1100, 800);
}

/* ------------------------------------------------------------------ *
 * Printer test page
 * ------------------------------------------------------------------ */
export function printTestPage(shop, printer = {}) {
  const size = printer.printer_type || "58mm";
  if (size === "A4") {
    return openDoc(`<!doctype html><html><head><meta charset="utf-8"/><title>Printer Test</title>
    <style>@page{size:A4;margin:14mm}${baseCss}</style></head><body>
    ${logoTag(shop, 60)}<h2>${shop?.name || "Bharat Digital Seva"} — A4 Printer Test</h2>
    <p>Sample Invoice: BDS/2026-27/000000<br/>Sample Product: Classmate Notebook 2 × ₹70.00 = ₹140.00</p>
    <p>Currency test: ₹1,00,000.00</p>
    <img src="${qrUrl("BDS:PRINTER:TEST", 6)}" width="120" height="120"/>
    <p class="muted">${shop?.invoice_footer || "Thank you for shopping with Bharat Digital Seva."}</p>
    ${autoPrint()}</body></html>`);
  }
  const sample = {
    invoice_no: "BDS/2026-27/000000",
    date: new Date().toISOString().slice(0, 10),
    time: new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }),
    customer_name: "Sample Customer",
    customer_mobile: "9999999999",
    cashier: "Printer Test",
    payment_method: "Cash",
    payment_status: "PAID",
    items: [
      { product_name: "Classmate Notebook (long product name wrap test)", stk_code: "STK000001", qty: 2, rate: 70, discount: 7, line_total: 133, tax: 0 },
      { product_name: "Blue Ball Pen", stk_code: "STK000002", qty: 3, rate: 10, discount: 0, line_total: 30, tax: 0 },
    ],
    subtotal: 170, auto_discount: 7, discount_percent: 5, manual_discount: 0, tax_amount: 0,
    round_off: 0, grand_total: 163, amount_paid: 163, change_due: 0, due_amount: 0,
    payments: [{ method: "Cash", amount: 163 }],
  };
  return openDoc(receiptHtml(sample, shop, printer, size, false), 420, 760);
}
